/**
 * The support desk (SUP-D1 to SUP-D12, docs/44 §3.7, docs/31 §3.3): every client request becomes a ticket that a
 * Workflow in `jobs` prepares with no person and no PC involved, and staff finish with one click in the console.
 *
 * These shapes are shared by `jobs` (which writes the brief, the risk list and the checklist into `support_tickets`)
 * and `console` (which reads them back and shows one brief per ticket). The brief is staff facing (British English is
 * fine in it); the reply is client facing (US English under the house voice rules).
 */
import { z } from 'zod';
import { Cursor, Email, IsoDateTime, Limit, text } from './common.ts';
import { ProposalState, RequestStatus, RequestType, Severity, SupportReplySource, SupportRisk, SupportRoute, SupportSource, SupportState } from './enums.ts';
import { ProposalId, RequestId, StaffId, SupportTicketId, TenantId, UserId } from './ids.ts';

// ------------------------------------------------------------------------------------------------ what jobs writes

/**
 * Why a ticket waits for a person, or what they should know before approving. `hold` risks need an admin to approve
 * (`requests.approve_any`) and are never answered by the AI: the reply is a fixed acknowledgement from code, or none
 * (docs/44 §3.2, 44-D12, 44-D13).
 */
export const SupportRiskCode = z.enum([
  'money',
  'refund',
  'guarantee',
  'cancel',
  'legal',
  'hostile',
  'life_event',
  'sensitive_field',
  'incident',
  'instructions',
  'low_confidence',
  'unverified_sender',
  'unknown_sender',
  'paused',
  'frozen',
  'out_of_scope',
]);
export type SupportRiskCode = z.infer<typeof SupportRiskCode>;

export const SupportRiskItem = z.object({ code: SupportRiskCode, why: z.string().max(300), hold: z.boolean() });
export type SupportRiskItem = z.infer<typeof SupportRiskItem>;

const ChecklistItemId = z.string().regex(/^c\d{1,2}$/).meta({ examples: ['c1'] });

export const SupportChecklistItem = z.object({
  id: ChecklistItemId,
  text: z.string().min(1).max(300),
  done: z.boolean(),
  doneBy: StaffId.nullable(),
  doneAt: IsoDateTime.nullable(),
});
export type SupportChecklistItem = z.infer<typeof SupportChecklistItem>;

export const SupportClassification = z.object({
  items: z
    .array(
      z.object({
        type: RequestType,
        ask: z.string().max(200),
        changeKind: z.string().max(20).nullable(),
        confidence: z.number().min(0).max(1),
      }),
    )
    .max(6),
  urgent: z.boolean(),
  flags: z.array(z.string().max(300)).max(12),
  /** Who sorted it: the model and prompt version, or `rule` when code decided (the classifier was unavailable). */
  by: z.string().max(80),
});
export type SupportClassification = z.infer<typeof SupportClassification>;

/** The client's context the Workflow gathered, as it was when the ticket was prepared. */
export const SupportBrief = z.object({
  business: z.object({
    name: z.string().max(80),
    status: z.string().max(20),
    plan: z.string().max(20).nullable(),
    billing: z.string().max(20).nullable(),
    approvalMode: z.string().max(20),
    timeZone: z.string().max(60).nullable(),
    goLiveAt: IsoDateTime.nullable(),
    paused: z.boolean(),
    frozen: z.boolean(),
  }),
  site: z.object({ host: z.string().max(253).nullable(), platformHost: z.string().max(253).nullable(), liveVersion: z.number().int().nullable() }),
  requester: z.object({ kind: z.enum(['owner', 'team', 'staff', 'unknown']), name: z.string().max(80).nullable(), role: z.string().max(20).nullable() }),
  facts: z.array(z.object({ path: z.string().max(120), value: z.string().max(300), status: z.string().max(20) })).max(24),
  services: z.array(z.string().max(60)).max(40),
  locations: z.array(z.object({ name: z.string().max(80), hasPlaceId: z.boolean(), googleAccess: z.string().max(30) })).max(10),
  history: z.array(z.object({ number: z.number().int(), subject: z.string().max(160).nullable(), type: z.string().max(4).nullable(), status: z.string().max(20), createdAt: IsoDateTime })).max(10),
  pastTickets: z.array(z.object({ number: z.number().int(), route: z.string().max(20).nullable(), state: z.string().max(20), createdAt: IsoDateTime })).max(6),
  openRequests: z.number().int().min(0),
  gatheredAt: IsoDateTime,
});
export type SupportBrief = z.infer<typeof SupportBrief>;

// ------------------------------------------------------------------------------------------------ the jobs RPC

/** How a ticket starts: which door the request came through and who the reply goes to (null: the owner). */
export const SupportStart = z
  .object({
    source: SupportSource,
    replyToUserId: UserId.nullable().optional(),
    /** A staff note to carry onto the ticket (the console paste: "forwarded from hello@"). */
    note: text(1000).optional(),
    /** Risks the door already knows about (an email whose sender failed DMARC, a pasted sender who isn't on the team). */
    risks: z.array(SupportRiskItem).max(4).optional(),
  })
  .strict();
export type SupportStart = z.infer<typeof SupportStart>;

/** What the console tells a waiting support Workflow. The ticket row already holds the final reply and checklist. */
export const SupportSignal = z.discriminatedUnion('action', [z.object({ action: z.literal('approved'), staffId: StaffId }).strict(), z.object({ action: z.literal('closed'), staffId: StaffId }).strict()]);
export type SupportSignal = z.infer<typeof SupportSignal>;

// ------------------------------------------------------------------------------------------------ the console

export const SupportQueueQuery = z
  .object({ state: SupportState.optional(), route: SupportRoute.optional(), risk: SupportRisk.optional(), mine: z.enum(['0', '1']).optional(), cursor: Cursor.optional(), limit: Limit.optional() })
  .strict();

export const SupportRow = z.object({
  id: SupportTicketId,
  tenantId: TenantId,
  tenantName: z.string().max(60),
  requestId: RequestId,
  number: z.number().int().positive(),
  subject: z.string().max(160).nullable(),
  type: RequestType.nullable(),
  requestStatus: RequestStatus,
  severity: Severity.nullable(),
  slaDueAt: IsoDateTime.nullable(),
  assignee: StaffId.nullable(),
  source: SupportSource,
  state: SupportState,
  route: SupportRoute.nullable(),
  risk: SupportRisk,
  replySource: SupportReplySource.nullable(),
  hasChange: z.boolean(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export const SupportQueue = z.object({ items: z.array(SupportRow), nextCursor: Cursor.nullable() });

const Diff = z.object({ label: z.string().max(160), before: z.string().max(2000).nullable(), after: z.string().max(2000).nullable() });

export const SupportDetail = z.object({
  ticket: SupportRow.extend({ preparedAt: IsoDateTime.nullable(), approvedAt: IsoDateTime.nullable(), sentAt: IsoDateTime.nullable(), closedAt: IsoDateTime.nullable(), note: z.string().max(1000).nullable() }),
  clientWords: z.string().max(5000),
  brief: SupportBrief.nullable(),
  classification: SupportClassification.nullable(),
  risks: z.array(SupportRiskItem),
  /** The change the pipeline drafted, never applied until Approve: the slot by slot diff and signed links to both versions. */
  change: z
    .object({
      proposalId: ProposalId,
      state: ProposalState,
      valid: z.boolean(),
      problems: z.array(z.string().max(300)).max(10),
      diff: z.array(Diff).max(50),
      page: z.string().max(200),
      before: z.object({ version: z.number().int().nullable(), url: z.string().max(1024).nullable() }),
      after: z.object({ version: z.number().int().nullable(), url: z.string().max(1024).nullable() }),
      needsClientOk: z.boolean(),
      googleChanges: z.array(z.string().max(160)).max(20),
    })
    .nullable(),
  checklist: z.array(SupportChecklistItem),
  reply: z.object({
    to: z.object({ name: z.string().max(80).nullable(), email: Email.nullable() }),
    subject: z.string().max(200).nullable(),
    body: z.string().max(5000).nullable(),
    source: SupportReplySource.nullable(),
    promptVersion: z.string().max(40).nullable(),
    flags: z.array(z.string().max(300)).max(12),
    /** House voice problems in the reply as it stands. Shown, never silently fixed. */
    lint: z.array(z.string().max(300)).max(20),
  }),
  approve: z.object({ allowed: z.boolean(), action: z.string().max(40), why: z.string().max(300).nullable() }),
  messages: z.array(z.object({ at: IsoDateTime, authorKind: z.string().max(20), body: z.string().max(5000), internal: z.boolean() })),
});

export const SupportReplyBody = z.object({ subject: text(200, 3), body: text(5000, 10) }).strict();
export const SupportApproveBody = z.object({ note: text(1000).optional() }).strict();
export const SupportCloseBody = z.object({ note: text(1000, 3) }).strict();
export const SupportChecklistBody = z.object({ itemId: ChecklistItemId, done: z.boolean() }).strict();

/** Staff paste an email (or a call note) that reached a person instead of the support address. The client is the path's. */
export const SupportIntakeBody = z
  .object({
    /** The address it came from, when there is one. Matched to the client's team; never stored on the ticket. */
    from: Email.optional(),
    subject: text(200).optional(),
    body: text(5000, 3),
    note: text(1000).optional(),
  })
  .strict();
export const SupportIntakeResult = z.object({ ticketId: SupportTicketId.nullable(), requestId: RequestId, number: z.number().int().positive(), started: z.boolean(), replyTo: z.enum(['sender', 'owner']) });

